import { useEffect, useState } from 'react';

import { Flag } from '@xing-com/flag';
import { IconSearch } from '@xing-com/icons';
import { Link } from '@xing-com/link';
import { BodyCopy } from '@xing-com/typography';

import { useSearchStore } from '../../context';
import { StyledInputBar, FilterContainer } from '../shared-styles';

type InputFilterProps = {
  defaultValue: string | number;
  name: string;
  label: string;
  placeholder: string;
  icon?: React.FC;
  upsellLink?: string;
};

export const InputFilter: React.FC<InputFilterProps> = ({
  defaultValue,
  name,
  label,
  placeholder,
  icon = IconSearch,
  upsellLink = '',
}) => {
  const setSearchParams = useSearchStore((store) => store.setSearchParams);
  const [inputValue, setInputValue] = useState(defaultValue);

  useEffect(() => {
    setInputValue(defaultValue);
  }, [defaultValue, setInputValue]);

  const onChange: React.FormEventHandler = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setInputValue(event.target.value);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' || event.key === 'Tab') {
      setSearchParams({
        [name]: inputValue,
      });
    }
  };

  const onClearInput = (): void => {
    setSearchParams({ [name]: '' });
  };

  return (
    <FilterContainer data-testid={name}>
      <BodyCopy fontWeight={'bold'} noMargin>
        {label}&ensp;
        {upsellLink.length ? (
          <Link to={upsellLink} data-testid="premium-upsell">
            <Flag size="small" variant="premium" />
          </Link>
        ) : null}
      </BodyCopy>
      <StyledInputBar
        clearButtonProps={{ 'aria-label': `Clear ${name} input field` }}
        icon={icon}
        disabled={Boolean(upsellLink)}
        size="small"
        placeholder={placeholder}
        variant="default"
        onChange={onChange}
        onBlur={() => setSearchParams({ [name]: inputValue })}
        onKeyDown={onKeyDown}
        onClearInput={onClearInput}
        value={inputValue.toString()}
      />
    </FilterContainer>
  );
};
